import { I18n } from 'react-redux-i18n';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';

import { MODULE_NAME } from '@modules/individuals';

import './profile-selector.styles.scss';

export const ProfileSelector = (props) => {
  const localModuleName = `${MODULE_NAME}-profile-selector-modal`;
  const { getData, onClose, onSubmit, showErrorMessage } = props;
  const [list, setList] = React.useState([]);
  const [value, setValue] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    getData()
      .then(setList)
      .catch((err) => {
        showErrorMessage(I18n.t('Failed to get profiles'));
        console.log(err);
      });
  }, []);

  const handleChange = React.useCallback((e) => {
    const name = e.currentTarget.dataset.name;

    setValue((prevValue) => prevValue.includes(name)
      ? prevValue.filter((x) => x !== name)
      : [...prevValue, name]
    );
  }, []);

  const renderCheckbox = React.useCallback(({ name }) => (
    <input type="checkbox" readOnly checked={value.includes(name)} />
  ), [value]);

  const columns = React.useMemo(() => [
    {
      key: 'name',
      width: '80%',
      field: 'name',
      name: I18n.t('Profile Name'),
    },
    {
      key: 'checkbox',
      field: 'name',
      render: renderCheckbox
    }
  ], [renderCheckbox]);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    try {
      const selectedItems = value.map((x) => list.find(({ name }) => name === x));
      await onSubmit(selectedItems);
    } catch (err) {
      setSubmitting(false);
      showErrorMessage(I18n.t('Failed to select profile'));
      console.error(err);
    }
  }, [value, onSubmit, showErrorMessage, list]);

  return (
    <Modal 
      show
      animation={false}
      bsSize="small"
      backdrop="static"
      bsClass="bootstrap modal"
      className="IndividualsProfileSelector"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes(localModuleName, 'header', 'title')}>
          {I18n.t('Select Profile')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Flex justify="space-between" alignItems="center" className="IndividualsProfileSelector__list-header">
          {columns.map((x) => (
            <span key={x.key} style={{ width: x.width }}>{x.name}</span>
          ))}
        </Flex>
        <div className="IndividualsProfileSelector__list">
          {list.map((item) => (
            <Flex 
              justify="space-between" 
              alignItems="center" 
              key={item.name} 
              className="IndividualsProfileSelector__list-row"
              data-name={item.name}
              onClick={handleChange}
              {...getTestAttributes(localModuleName, 'profile', 'row')}
            >
              {columns.map((col) => (
                <React.Fragment key={col.key}>
                  <span style={{ width: col.width }}>{col.render ? col.render(item) : item[col.field]}</span>
                </React.Fragment>
              ))}
            </Flex>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button 
          disabled={submitting}
          {...getTestAttributes(localModuleName, 'cancel', 'button')}
          onClick={onClose}
        >
          {I18n.t('Cancel')}
        </Button>
        <Button 
          bsStyle="primary"
          disabled={!value.length || submitting}
          {...getTestAttributes(localModuleName, 'submit', 'button')}
          onClick={handleSubmit}
        >
          {submitting ? I18n.t('Loading') : I18n.t('Apply')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};